import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import cx from 'classnames';
import PageHeader from '../PageHeader';
import PageShare from '../PageShare';
import PageTopbar from '../PageTopbar';
import MoreTemplateButton from '../MoreTemplateButton';
import { getTemplatesBatch } from '@/apiRequest/template';
import Styles from './index.module.scss';

const TemplateArea = dynamic(import('../TemplateArea'), { ssr: false });

const TemplatePage = ({
  data,
  isLight,
  isPageVisible,
  isIdle,
  onChangeIdle,
  onSlideNext,
  onRedirect,
  onSensorTrack,
}) => {
  const [tpls, setTpls] = useState([]);
  const [currentTpl, setCurrentTpl] = useState();

  const {
    general, header, common, templates, currentPageIndex, currentPageSlug, pageHeight, pageIndex, pagesLength,
  } = data;

  const { slug: uid, pageBg, pageBgImage } = common;
  const { templates: resources = [] } = templates;
  const { title = {} } = header;

  const codeCanvasMap = {};
  resources.forEach(({ code, canvas }) => {
    codeCanvasMap[code] = canvas;
  });

  const getTargetCanvasUrl = (scripts, code) => {
    let coverUrl, videoUrl = '', segmentCount = 0, duration = 0, canvasType = '';
    scripts.forEach(({ is_default, canvas_type, cover_url, video_url, segment_count, duration: d }) => {
      if (canvas_type === codeCanvasMap[code]) {
        coverUrl = cover_url.medium;
        videoUrl = video_url.medium;
        segmentCount = segment_count;
        duration = d;
        canvasType = canvas_type;
      }
      else if (is_default && !videoUrl && !coverUrl) {
        coverUrl = cover_url.medium;
        videoUrl = video_url.medium;
        segmentCount = segment_count;
        duration = d;
        canvasType = canvas_type;
      }
    });

    return { coverUrl, videoUrl, segmentCount, duration, canvasType };
  };

  const fetchTemplates = async () => {
    const codesString = resources.map(({ code }) => code).toString();
    const data = await getTemplatesBatch({ codes: codesString }) || [];

    const res = data.map(({ scripts, code, ...rest }) => {
      const { coverUrl, videoUrl, segmentCount, duration, canvasType } = getTargetCanvasUrl(scripts, code);
      return {
        ...rest,
        code,
        coverUrl,
        videoUrl,
        segmentCount,
        duration,
        canvasType,
      };
    });
    setTpls(res);

    const firstTpl = res[0] || {};
    setCurrentTpl(firstTpl);
    onChangeIdle(true);
  };

  useEffect(() => {
    const shouldFetch = resources.length > 0 && tpls.length < 1;
    if (isPageVisible && shouldFetch) {
      fetchTemplates();
    }
    if (!isPageVisible && shouldFetch && isIdle && pageIndex === currentPageIndex + 1) {
      onChangeIdle(false);
      fetchTemplates();
    }
  }, [isPageVisible, isIdle, currentPageIndex, resources]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSelectTpl = (val) => setCurrentTpl(val);

  return (
    <div
      id={uid}
      className={cx(Styles.page, { [Styles.active]: uid === currentPageSlug })}
      style={{
        height: pageHeight,
        backgroundColor: pageBg,
        backgroundImage: `url(${pageBgImage})`,
        transform: `translateY(-${pageIndex * pageHeight}px)`,
        zIndex: pagesLength - pageIndex,
      }}
    >
      {
        isLight
          ? <PageShare className={Styles.pageShare} shareInfo={common?.share} pageSlug={uid} pageTitle={title?.label} onSensorTrack={onSensorTrack} />
          : <PageTopbar className={Styles.pageTopbar} onSensorTrack={onSensorTrack} onRedirect={onRedirect} />
      }
      <PageHeader className={cx(Styles.pageHeader, { [Styles.notLight]: !isLight })} headerInfo={header} />
      <TemplateArea
        className={cx(Styles.templateArea, { [Styles.notLight]: !isLight })}
        isVisible={isPageVisible}
        isLight={isLight}
        pageSlug={uid}
        pageTitle={title?.label}
        tpls={tpls}
        selectedTpl={currentTpl}
        templatesInfo={templates}
        onSelect={onSelectTpl}
        onRedirect={onRedirect}
        onSensorTrack={onSensorTrack}
      />
      <MoreTemplateButton
        className={Styles.moreButton}
        isLight={isLight}
        isLast={pageIndex === pagesLength - 1}
        pageTitle={title?.label}
        selectedTpl={currentTpl}
        createButtonProps={general.bottomButton}
        moreButtonProps={templates.moreTemplate}
        onSlideNext={onSlideNext}
        onRedirect={onRedirect}
        onSensorTrack={onSensorTrack}
      />
      <div className={cx(Styles.gap, {[Styles.isLight]: isLight})} />
    </div>
  );
};

TemplatePage.propTypes = {
  data: PropTypes.shape({}),
  isLight: PropTypes.bool,
  isPageVisible: PropTypes.bool,
  isIdle: PropTypes.bool,
  onChangeIdle: PropTypes.func,
  onSlideNext: PropTypes.func,
  onRedirect: PropTypes.func,
  onSensorTrack: PropTypes.func,
};

TemplatePage.defaultProps = {
  data: {
    general: {},
    common: {},
    header: {},
    templates: {},
  },
  isLight: false,
  isPageVisible: false,
  isIdle: false,
  onChangeIdle: () => true,
  onSlideNext: () => true,
  onRedirect: () => true,
  onSensorTrack: () => true,
};

export default TemplatePage;
